/**
 * Client-side Datei-Export (Kommentare C — „Download von Dateien").
 *   Listen  → CSV · PDF (Excel = Server-Konverter, siehe unten)
 *   Notizen → .md · PDF (Word/Docx = Server-Konverter)
 *   Boards  → PNG/SVG/Excalidraw (nativer Host-Export) · JPG · PDF
 * CC_09 P10 ergänzt PDF (abhängigkeitsfrei, hand-geschriebener Writer) und JPG.
 * Excel (xlsx) und Word (docx) bleiben bewusst ein eigener Server-Konverter-
 * Schritt: sie brauchen ZIP+OOXML und wären ein schwergewichtiger Bundle-
 * Dependency — abgeschnitten unter `[CC_09 ABSCHNEIDE v1.5]`, gemeldet als
 * eigener Rest. Reine Funktionen + DOM-Download-Helfer (Text und Binär).
 */

const NEEDS_QUOTE = /[",;\t\n\r]/;

/**
 * Zellwert → Text (Revision R3): Listen-Zellen können OBJEKTE tragen
 * (url_text = {text, url}) — `String(v)` ergäbe "[object Object]" im CSV wie
 * im PDF. Zentraler Formatter für beide Exporte.
 */
export const cellText = (value: unknown): string => {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "object") {
    const o = value as { text?: unknown; url?: unknown };
    const text = o.text ? String(o.text) : "";
    const url = o.url ? String(o.url) : "";
    if (text || url) {
      return text && url ? `${text} (${url})` : text || url;
    }
    return JSON.stringify(value);
  }
  return String(value);
};

/** RFC-4180 field escaping. */
export const csvEscape = (value: unknown): string => {
  const s = cellText(value);
  return NEEDS_QUOTE.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export interface CsvColumn {
  id: string;
  name: string;
}
export interface CsvRow {
  id: string;
  seq?: number;
  data: Record<string, unknown>;
}

/**
 * Serialise a list to CSV. The two read-only system columns come first
 * (row_id = seq, UUID = id), then the user columns in their given order.
 */
export const toCSV = (columns: CsvColumn[], rows: CsvRow[]): string => {
  const header = ["row_id", "UUID", ...columns.map((c) => c.name)];
  const lines = [header.map(csvEscape).join(",")];
  for (const r of rows) {
    const cells: unknown[] = [
      r.seq ?? "",
      r.id,
      ...columns.map((c) => r.data[c.id]),
    ];
    lines.push(cells.map(csvEscape).join(","));
  }
  return lines.join("\r\n");
};

/** Turn an arbitrary name into a safe download filename with an extension. */
export const safeFilename = (name: string, ext: string): string => {
  const base = (name || "export")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .trim()
    .toLowerCase();
  return base.endsWith(`.${ext}`) ? base : `${base}.${ext}`;
};

/**
 * Trennzeichen-Erkennung (Revision R3): deutsche Excel-CSVs nutzen `;` statt
 * `,` — vorher landete so die GANZE Zeile in einer Spalte. Gezählt wird auf der
 * ersten nicht-leeren Zeile außerhalb von Anführungszeichen; bei Gleichstand
 * gewinnt das Komma (RFC 4180).
 */
export const detectDelimiter = (text: string): "," | ";" | "\t" => {
  const firstLine = (text.split(/\r?\n/).find((l) => l.trim() !== "") ?? "")
    // quoted Abschnitte ausblenden, damit Kommata IN Werten nicht zählen
    .replace(/"[^"]*"/g, "");
  const counts: Array<["," | ";" | "\t", number]> = [
    [",", (firstLine.match(/,/g) || []).length],
    [";", (firstLine.match(/;/g) || []).length],
    ["\t", (firstLine.match(/\t/g) || []).length],
  ];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ",";
};

/**
 * Datei-Bytes → CSV-Text (Revision R3): UTF-8-BOM entfernen; ist der Inhalt
 * kein gültiges UTF-8 (Excel/Windows exportiert oft CP-1252), wird als
 * windows-1252 dekodiert — vorher wurden Umlaute zu U+FFFD zerlegt.
 */
export const decodeCsvBuffer = (buf: ArrayBuffer): string => {
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(buf);
  } catch {
    text = new TextDecoder("windows-1252").decode(buf);
  }
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
};

/**
 * Parse CSV text into a matrix of string cells (RFC-4180: quoted fields,
 * doubled quotes, CRLF or LF line endings). Fully-empty lines are dropped.
 * Das Trennzeichen ist parametrierbar (Standard Komma; siehe detectDelimiter).
 */
export const parseCSV = (
  text: string,
  delimiter: "," | ";" | "\t" = ",",
): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") {
        i++;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
};

/** Trigger a browser download of text content (no server round-trip). */
export const downloadText = (
  filename: string,
  mime: string,
  content: string,
): void => {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** Trigger a browser download of binary content (Blob/Uint8Array). */
export const downloadBytes = (
  filename: string,
  mime: string,
  bytes: Uint8Array,
): void => {
  // .buffer statt der View: umgeht die strengere BlobPart-Typisierung neuerer
  // TS-lib.dom (Uint8Array<ArrayBufferLike> ≠ ArrayBuffer). Unsere Bytes sind
  // stets offset-0 über ihren vollen Buffer, also verlustfrei.
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/* ---- PDF (CC_09 P10) -------------------------------------------------------
 * Minimaler, abhängigkeitsfreier PDF-Writer. Der approved Design-Stand (siehe
 * Datei-Kopf) führt Excel/Word über einen Server-Konverter; PDF dagegen ist
 * clientseitig ohne Bibliothek erzeugbar und wird hier gebaut (kein neuer
 * Bundle-Dependency). Latin-1-Ausgabe: Text-PDF für Listen/Notizen, ein
 * JPEG-Embed (DCTDecode) für Boards.
 */

const latin1Bytes = (s: string): Uint8Array => {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    out[i] = c > 0xff ? 0x3f /* '?' */ : c;
  }
  return out;
};

const concatBytes = (chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
};

/**
 * WinAnsi-Zusatzzeichen (Revision R3): der Font ist /WinAnsiEncoding — dort
 * liegen €, „ " " – — … • etc. auf 0x80–0x9F. Vorher wurden sie als >0xFF zu
 * "?" gefaltet (eine €-Währungsspalte exportierte als "? 1.234,5").
 */
const WINANSI_EXTRA: Record<string, number> = {
  "€": 0x80,
  "‚": 0x82,
  ƒ: 0x83,
  "„": 0x84,
  "…": 0x85,
  "†": 0x86,
  "‡": 0x87,
  ˆ: 0x88,
  "‰": 0x89,
  Š: 0x8a,
  "‹": 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  "‘": 0x91,
  "’": 0x92,
  "“": 0x93,
  "”": 0x94,
  "•": 0x95,
  "–": 0x96,
  "—": 0x97,
  "˜": 0x98,
  "™": 0x99,
  š: 0x9a,
  "›": 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f,
};

/** Escape a string for a PDF literal `(...)` and fold to WinAnsi/Latin-1. */
export const pdfEscapeText = (s: string): string =>
  Array.from(s)
    .map((ch) => {
      if (ch === "\\" || ch === "(" || ch === ")") {
        return `\\${ch}`;
      }
      const mapped = WINANSI_EXTRA[ch];
      if (mapped !== undefined) {
        return String.fromCharCode(mapped);
      }
      const code = ch.codePointAt(0) ?? 0x3f;
      return code > 0xff ? "?" : ch;
    })
    .join("");

/**
 * Assemble a PDF from pre-serialised object bodies (objects[0] = object 1).
 * Handles the cross-reference table + trailer and returns the final bytes.
 * Object bodies may be text (Latin-1) or raw bytes (for stream data).
 */
const assemblePdf = (objects: (string | Uint8Array)[]): Uint8Array => {
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let pos = 0;
  const push = (chunk: string | Uint8Array) => {
    const b = typeof chunk === "string" ? latin1Bytes(chunk) : chunk;
    parts.push(b);
    pos += b.length;
  };
  push("%PDF-1.4\n%\xe2\xe3\xcf\xd3\n");
  objects.forEach((body, i) => {
    offsets[i] = pos;
    push(`${i + 1} 0 obj\n`);
    push(body);
    push("\nendobj\n");
  });
  const xrefPos = pos;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const off of offsets) {
    xref += `${off.toString().padStart(10, "0")} 00000 n \n`;
  }
  push(xref);
  push(
    `trailer\n<< /Size ${
      objects.length + 1
    } /Root 1 0 R >>\nstartxref\n${xrefPos}\n%%EOF\n`,
  );
  return concatBytes(parts);
};

const A4 = { w: 595, h: 842 };

/**
 * A text PDF (Helvetica) from plain lines, paginated so nothing is silently
 * dropped. Long lines are hard-wrapped at a character budget. Revision R3:
 * Seitenformat + Ränder sind parametrierbar (Dokumenteinstellungen, md-doc).
 */
export const textToPdf = (
  title: string,
  lines: string[],
  opts: {
    fontSize?: number;
    pageW?: number;
    pageH?: number;
    margin?: number;
  } = {},
): Uint8Array => {
  const size = opts.fontSize ?? 11;
  const pageW = opts.pageW ?? A4.w;
  const pageH = opts.pageH ?? A4.h;
  const leading = size * 1.35;
  const marginX = opts.margin ?? 48;
  const marginTop = (opts.margin ?? 48) + 8;
  const usableHeight = pageH - marginTop - (opts.margin ?? 48);
  const linesPerPage = Math.max(1, Math.floor(usableHeight / leading));
  const maxChars = Math.max(
    8,
    Math.floor((pageW - 2 * marginX) / (size * 0.5)),
  );

  const wrapped: string[] = [];
  const all = title ? [title, "", ...lines] : lines;
  for (const raw of all) {
    const line = raw ?? "";
    if (line.length <= maxChars) {
      wrapped.push(line);
    } else {
      for (let i = 0; i < line.length; i += maxChars) {
        wrapped.push(line.slice(i, i + maxChars));
      }
    }
  }
  const pages: string[][] = [];
  for (let i = 0; i < wrapped.length; i += linesPerPage) {
    pages.push(wrapped.slice(i, i + linesPerPage));
  }
  if (pages.length === 0) {
    pages.push([""]);
  }

  // Object plan: 1 Catalog, 2 Pages, 3 Font, then per page a Page + Contents.
  const fontObj = 3;
  const objects: (string | Uint8Array)[] = [];
  const pageObjNums: number[] = [];
  pages.forEach((_p, idx) => pageObjNums.push(4 + idx * 2));

  objects[0] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[1] = `<< /Type /Pages /Count ${pages.length} /Kids [${pageObjNums
    .map((n) => `${n} 0 R`)
    .join(" ")}] >>`;
  objects[2] = `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>`;

  pages.forEach((pageLines, idx) => {
    const pageObj = 4 + idx * 2;
    const contentObj = pageObj + 1;
    let stream = `BT /F1 ${size} Tf ${leading} TL ${marginX} ${
      pageH - marginTop
    } Td\n`;
    pageLines.forEach((ln, li) => {
      stream += `(${pdfEscapeText(ln)}) Tj`;
      stream += li < pageLines.length - 1 ? " T*\n" : "\n";
    });
    stream += "ET";
    objects[pageObj - 1] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] ` +
      `/Resources << /Font << /F1 ${fontObj} 0 R >> >> /Contents ${contentObj} 0 R >>`;
    objects[
      contentObj - 1
    ] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });

  return assemblePdf(objects);
};

/** JPEG dimensions from its SOF marker (baseline/progressive). 0×0 if absent. */
export const jpegSize = (
  bytes: Uint8Array,
): { width: number; height: number } => {
  let i = 2; // skip SOI
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) {
      i++;
      continue;
    }
    const marker = bytes[i + 1];
    // SOF0..SOF3, SOF5..SOF7, SOF9..SOF11, SOF13..SOF15 carry dimensions.
    if (
      (marker >= 0xc0 && marker <= 0xc3) ||
      (marker >= 0xc5 && marker <= 0xc7) ||
      (marker >= 0xc9 && marker <= 0xcb) ||
      (marker >= 0xcd && marker <= 0xcf)
    ) {
      const height = (bytes[i + 5] << 8) | bytes[i + 6];
      const width = (bytes[i + 7] << 8) | bytes[i + 8];
      return { width, height };
    }
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    i += 2 + len;
  }
  return { width: 0, height: 0 };
};

/**
 * A single-page PDF that embeds a JPEG (DCTDecode), scaled to fit A4 with a
 * margin and centred. Used for board → PDF (board is exported to JPEG first).
 */
export const jpegToPdf = (jpeg: Uint8Array): Uint8Array => {
  const { width, height } = jpegSize(jpeg);
  const w = width || 1;
  const h = height || 1;
  const margin = 36;
  const maxW = A4.w - 2 * margin;
  const maxH = A4.h - 2 * margin;
  const scale = Math.min(maxW / w, maxH / h, 1);
  const drawW = w * scale;
  const drawH = h * scale;
  const x = (A4.w - drawW) / 2;
  const y = (A4.h - drawH) / 2;

  const content = `q ${drawW.toFixed(2)} 0 0 ${drawH.toFixed(2)} ${x.toFixed(
    2,
  )} ${y.toFixed(2)} cm /Im0 Do Q`;

  const objects: (string | Uint8Array)[] = [];
  objects[0] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[1] = "<< /Type /Pages /Count 1 /Kids [3 0 R] >>";
  objects[2] =
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4.w} ${A4.h}] ` +
    `/Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>`;
  objects[3] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  objects[4] = concatBytes([
    latin1Bytes(
      `<< /Type /XObject /Subtype /Image /Width ${w} /Height ${h} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ` +
        `/Length ${jpeg.length} >>\nstream\n`,
    ),
    jpeg,
    latin1Bytes("\nendstream"),
  ]);
  return assemblePdf(objects);
};

/**
 * Build the plain text lines of a list for the text-PDF exporter. Revision R3:
 * Zellwerte über `cellText` (url_text-Objekte, vorher "[object Object]"); die
 * UUID-Spalte bleibt dem CSV vorbehalten — im Papierformat frisst sie nur
 * Breite und zwang jede Zeile in den Umbruch.
 */
export const listToPdfLines = (
  columns: CsvColumn[],
  rows: CsvRow[],
): string[] => {
  const header = ["row_id", ...columns.map((c) => c.name)].join(" | ");
  const out = [header];
  for (const r of rows) {
    const cells = [
      String(r.seq ?? ""),
      ...columns.map((c) => cellText(r.data[c.id])),
    ];
    out.push(cells.join(" | "));
  }
  return out;
};

/**
 * Flatten markdown to plain text lines for the text-PDF exporter. Revision R3:
 * Links werden zu "Label (URL)" statt roh als "[Label](URL)" stehenzubleiben.
 */
export const mdToPdfLines = (md: string): string[] =>
  (md ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) =>
      l
        .replace(/^#{1,6}\s+/, "")
        .replace(/^\s*[-*+]\s+/, "• ")
        // Bilder ZUERST auf einen Platzhalter reduzieren — sonst würde die
        // Link-Regex unten die (u. U. riesige) `data:image/…;base64,…`-Quelle
        // als Text ins PDF schreiben („base64-Blob statt Bild"-Befund). Der
        // Text-PDF-Writer kann keine beliebigen Inline-Bilder einbetten.
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, (_m, alt) =>
          alt ? `[Bild: ${alt}]` : "[Bild]",
        )
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, "$1 ($2)")
        .replace(/[*_`]/g, "")
        .replace(/^>\s?/, "„ "),
    );

/* ---- Markdown → PDF WITH embedded images ---------------------------------
 * The text-only `mdToPdfLines` path reduces every image to a "[Bild: …]"
 * placeholder because the plain text writer can't embed images. `mdToPdf`
 * fixes that: it interleaves the flattened text with the actual images,
 * embedded as JPEG XObjects (DCTDecode, the same encoding boards use). Images
 * are decoded browser-side through a canvas (any format the browser can draw —
 * PNG, JPEG, WebP, …), so this is async. An image that can't be decoded (e.g. a
 * tainted cross-origin URL) falls back to the "[Bild: …]" placeholder for that
 * one image; the base64 source is NEVER written into the PDF as text.
 */

export interface DecodedImage {
  jpeg: Uint8Array;
  width: number;
  height: number;
}
/** Decode one image source to JPEG bytes + natural size, or null on failure. */
export type ImageLoader = (src: string) => Promise<DecodedImage | null>;

const base64ToBytes = (b64: string): Uint8Array => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
};

/** Decode a `data:` URL's payload to bytes (base64 or percent-encoded). */
export const dataUrlToBytes = (dataUrl: string): Uint8Array => {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) {
    return new Uint8Array(0);
  }
  const meta = dataUrl.slice(0, comma);
  const data = dataUrl.slice(comma + 1);
  return meta.includes(";base64")
    ? base64ToBytes(data)
    : latin1Bytes(decodeURIComponent(data));
};

/**
 * Default browser image loader: draw the source onto a white canvas (JPEG has
 * no alpha, so transparent PNGs stay readable) and re-encode as JPEG. Returns
 * null if the image can't be loaded or the canvas is unavailable/tainted.
 */
const canvasImageLoader: ImageLoader = async (src) => {
  try {
    // For non-data: sources (an uploaded note image served from /api or S3), load
    // via a fetched blob object-URL instead of a cross-origin <img>. A blob URL is
    // same-origin, so the canvas is NOT tainted and toDataURL succeeds — this is
    // why images previously fell back to the "[Bild: …]" placeholder (P18). data:
    // URLs are used directly (they never taint).
    let loadSrc = src;
    let objectUrl: string | null = null;
    if (!src.startsWith("data:")) {
      const res = await fetch(src, { credentials: "include" });
      if (!res.ok) {
        return null;
      }
      const blob = await res.blob();
      objectUrl = URL.createObjectURL(blob);
      loadSrc = objectUrl;
    }
    let img: HTMLImageElement;
    try {
      img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error("image load failed"));
        im.src = loadSrc;
      });
    } finally {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) {
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0);
    const jpegUrl = canvas.toDataURL("image/jpeg", 0.85);
    if (!jpegUrl.startsWith("data:image/jpeg")) {
      return null;
    }
    return { jpeg: dataUrlToBytes(jpegUrl), width, height };
  } catch {
    return null;
  }
};

// One document item after flattening: a run of text, or an image reference.
type MdItem =
  | { type: "text"; text: string }
  | { type: "image"; alt: string; src: string };

// Flatten the non-image markdown of a single line (same rules as mdToPdfLines,
// minus the image handling — images are already carried as separate items).
const flattenTextLine = (l: string): string =>
  l
    .replace(/^#{1,6}\s+/, "")
    .replace(/^\s*[-*+]\s+/, "• ")
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, "$1 ($2)")
    .replace(/[*_`]/g, "")
    .replace(/^>\s?/, "„ ");

/** Split markdown into an ordered list of text / image items (order preserved). */
export const mdToItems = (md: string): MdItem[] => {
  const items: MdItem[] = [];
  for (const rawLine of (md ?? "").replace(/\r\n/g, "\n").split("\n")) {
    // Walk the image matches by index and flatten the text spans between them,
    // so the (potentially huge) data: source never enters the text stream.
    const imgRe = /!\[([^\]]*)\]\(([^)]*)\)/g;
    let last = 0;
    let anyImage = false;
    const pieces: MdItem[] = [];
    let m: RegExpExecArray | null = imgRe.exec(rawLine);
    while (m !== null) {
      anyImage = true;
      const before = rawLine.slice(last, m.index);
      if (before.trim() !== "") {
        pieces.push({ type: "text", text: flattenTextLine(before) });
      }
      pieces.push({ type: "image", alt: m[1] || "", src: m[2] || "" });
      last = m.index + m[0].length;
      m = imgRe.exec(rawLine);
    }
    if (!anyImage) {
      items.push({ type: "text", text: flattenTextLine(rawLine) });
      continue;
    }
    const after = rawLine.slice(last);
    if (after.trim() !== "") {
      pieces.push({ type: "text", text: flattenTextLine(after) });
    }
    items.push(...pieces);
  }
  return items;
};

/**
 * Markdown → PDF with the images actually embedded (not "[Bild: …]"). Async
 * because images are decoded through the browser. `loadImage` is injectable for
 * tests; in production it defaults to the canvas-based loader.
 */
export const mdToPdf = async (
  title: string,
  md: string,
  opts: {
    fontSize?: number;
    pageW?: number;
    pageH?: number;
    margin?: number;
  } = {},
  loadImage: ImageLoader = canvasImageLoader,
): Promise<Uint8Array> => {
  const size = opts.fontSize ?? 11;
  const pageW = opts.pageW ?? A4.w;
  const pageH = opts.pageH ?? A4.h;
  const leading = size * 1.35;
  const marginX = opts.margin ?? 48;
  const marginTop = (opts.margin ?? 48) + 8;
  const bottomY = opts.margin ?? 48;
  const topY = pageH - marginTop;
  const usableW = pageW - 2 * marginX;
  const usableH = topY - bottomY;
  const maxChars = Math.max(8, Math.floor(usableW / (size * 0.5)));
  const imgGap = leading * 0.5;

  const items: MdItem[] = [];
  if (title) {
    items.push({ type: "text", text: title }, { type: "text", text: "" });
  }
  items.push(...mdToItems(md));

  // Decode each distinct image once; a failed decode becomes a placeholder.
  const images: DecodedImage[] = [];
  const srcToIndex = new Map<string, number>();
  for (const it of items) {
    if (it.type !== "image" || srcToIndex.has(it.src)) {
      continue;
    }
    const decoded = await loadImage(it.src);
    if (decoded) {
      srcToIndex.set(it.src, images.length);
      images.push(decoded);
    }
  }

  type PageText = { x: number; y: number; s: string };
  type PageImg = {
    imgIndex: number;
    x: number;
    y: number;
    w: number;
    h: number;
  };
  const pages: Array<{ texts: PageText[]; imgs: PageImg[] }> = [];
  let page: { texts: PageText[]; imgs: PageImg[] } = { texts: [], imgs: [] };
  pages.push(page);
  let cursorY = topY;
  const newPage = () => {
    page = { texts: [], imgs: [] };
    pages.push(page);
    cursorY = topY;
  };

  const placeTextLine = (s: string) => {
    if (cursorY < bottomY) {
      newPage();
    }
    page.texts.push({ x: marginX, y: cursorY, s });
    cursorY -= leading;
  };

  const placeImage = (imgIndex: number, natW: number, natH: number) => {
    const scale = Math.min(usableW / natW, 1);
    let drawW = natW * scale;
    let drawH = natH * scale;
    if (drawH > usableH) {
      const s2 = usableH / drawH;
      drawW *= s2;
      drawH *= s2;
    }
    // Start a fresh page if the image would overflow the current one (unless the
    // page is empty — then it simply fills a page).
    if (
      cursorY - drawH < bottomY &&
      (page.texts.length > 0 || page.imgs.length > 0)
    ) {
      newPage();
    }
    const bottom = cursorY - drawH;
    page.imgs.push({ imgIndex, x: marginX, y: bottom, w: drawW, h: drawH });
    cursorY = bottom - imgGap;
  };

  for (const it of items) {
    if (it.type === "image") {
      const idx = srcToIndex.get(it.src);
      if (idx === undefined) {
        // Undecodable image → the placeholder (never the base64 source).
        placeTextLine(it.alt ? `[Bild: ${it.alt}]` : "[Bild]");
      } else {
        placeImage(idx, images[idx].width, images[idx].height);
      }
      continue;
    }
    const line = it.text ?? "";
    if (line.length <= maxChars) {
      placeTextLine(line);
    } else {
      for (let i = 0; i < line.length; i += maxChars) {
        placeTextLine(line.slice(i, i + maxChars));
      }
    }
  }

  // Object plan: 1 Catalog, 2 Pages, 3 Font, 4..3+N image XObjects, then per
  // page a Page + Contents object.
  const n = images.length;
  const firstPageObj = 4 + n;
  const pageObjNums = pages.map((_p, p) => firstPageObj + p * 2);
  const imgObjNum = (i: number) => 4 + i;

  const objects: (string | Uint8Array)[] = [];
  objects[0] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[1] = `<< /Type /Pages /Count ${pages.length} /Kids [${pageObjNums
    .map((num) => `${num} 0 R`)
    .join(" ")}] >>`;
  objects[2] = `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>`;
  images.forEach((im, i) => {
    objects[3 + i] = concatBytes([
      latin1Bytes(
        `<< /Type /XObject /Subtype /Image /Width ${im.width} /Height ${im.height} ` +
          `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ` +
          `/Length ${im.jpeg.length} >>\nstream\n`,
      ),
      im.jpeg,
      latin1Bytes("\nendstream"),
    ]);
  });

  pages.forEach((pg, p) => {
    const pageObj = firstPageObj + p * 2;
    const contentObj = pageObj + 1;
    let content = "";
    if (pg.texts.length > 0) {
      content += `BT /F1 ${size} Tf\n`;
      for (const t of pg.texts) {
        content += `1 0 0 1 ${t.x.toFixed(2)} ${t.y.toFixed(
          2,
        )} Tm (${pdfEscapeText(t.s)}) Tj\n`;
      }
      content += "ET\n";
    }
    for (const im of pg.imgs) {
      content += `q ${im.w.toFixed(2)} 0 0 ${im.h.toFixed(2)} ${im.x.toFixed(
        2,
      )} ${im.y.toFixed(2)} cm /Im${im.imgIndex} Do Q\n`;
    }
    const imgsOnPage = [...new Set(pg.imgs.map((im) => im.imgIndex))];
    const xobjRes =
      imgsOnPage.length > 0
        ? ` /XObject << ${imgsOnPage
            .map((idx) => `/Im${idx} ${imgObjNum(idx)} 0 R`)
            .join(" ")} >>`
        : "";
    objects[pageObj - 1] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] ` +
      `/Resources << /Font << /F1 3 0 R >>${xobjRes} >> /Contents ${contentObj} 0 R >>`;
    objects[
      contentObj - 1
    ] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });

  return assemblePdf(objects);
};
